import { Check, ChevronRight, Minus, X } from 'lucide-react'
import type { BulkRowOutcome } from './types'

/** Groups outcomes by reason so the summary scales to large selections. */
function groupByReason(items: BulkRowOutcome[]): [string, BulkRowOutcome[]][] {
  const groups = new Map<string, BulkRowOutcome[]>()
  for (const item of items) {
    const reason = item.reason || `Failed`
    const list = groups.get(reason) ?? []
    list.push(item)
    groups.set(reason, list)
  }
  return [...groups.entries()]
}

function NameList({ items }: { items: BulkRowOutcome[] }) {
  return (
    <ul className="ml-6 mt-1.5 list-disc text-xs text-subtle">
      {items.map((o) => (
        <li key={o.id || o.label}>{o.label}</li>
      ))}
    </ul>
  )
}

/**
 * The counts and per-reason groups of a finished bulk run. Failures are grouped by reason (count +
 * expandable names); skips get their own amber bucket rather than counting as failures; successes
 * collapse to a single expandable count, so 5 or 500 stay readable.
 */
export function BulkResultsSummary({ outcomes, successWord }: { outcomes: BulkRowOutcome[]; successWord: string }) {
  const failed = outcomes.filter((o) => !o.ok && !o.skipped)
  const skipped = outcomes.filter((o) => !o.ok && o.skipped)
  const succeeded = outcomes.filter((o) => o.ok)
  const failureGroups = groupByReason(failed)
  const skippedGroups = groupByReason(skipped)

  return (
    <>
      <p className="text-ui-sm text-subtle">
        <span className="font-semibold text-strong">{succeeded.length}</span> {successWord}
        {skipped.length > 0 && (
          <>
            {` · `}
            <span className="font-semibold text-warning-default">{skipped.length}</span> skipped
          </>
        )}
        {failed.length > 0 && (
          <>
            {` · `}
            <span className="font-semibold text-red-600">{failed.length}</span> failed
          </>
        )}
      </p>

      <div className="flex max-h-80 flex-col gap-2 overflow-y-auto">
        {failureGroups.map(([reason, items]) => (
          <details key={reason} open={failed.length <= 5} className="group rounded-md bg-red-50 px-3 py-2 text-sm">
            <summary className="flex cursor-pointer list-none items-start gap-2 [&::-webkit-details-marker]:hidden">
              <X className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
              <span className="flex-1">
                <span className="font-medium text-strong">
                  {items.length} {items.length === 1 ? `contact` : `contacts`} failed
                </span>
                <span className="block text-xs text-red-700">{reason}</span>
              </span>
              <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-minimal transition-transform group-open:rotate-90" />
            </summary>
            <NameList items={items} />
          </details>
        ))}

        {skippedGroups.map(([reason, items]) => (
          <details
            key={reason}
            open={skipped.length <= 5}
            className="group rounded-md bg-warning-minimal px-3 py-2 text-sm"
          >
            <summary className="flex cursor-pointer list-none items-start gap-2 [&::-webkit-details-marker]:hidden">
              <Minus className="mt-0.5 h-4 w-4 shrink-0 text-warning-default" />
              <span className="flex-1">
                <span className="font-medium text-strong">
                  {items.length} {items.length === 1 ? `contact` : `contacts`} skipped
                </span>
                <span className="block text-xs text-warning-strong">{reason}</span>
              </span>
              <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-minimal transition-transform group-open:rotate-90" />
            </summary>
            <NameList items={items} />
          </details>
        ))}

        {succeeded.length > 0 && (
          <details className="group rounded-md bg-success-minimal px-3 py-2 text-sm">
            <summary className="flex cursor-pointer list-none items-center gap-2 [&::-webkit-details-marker]:hidden">
              <Check className="h-4 w-4 shrink-0 text-success-default" />
              <span className="flex-1 font-medium text-strong">
                {succeeded.length} {successWord}
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-minimal transition-transform group-open:rotate-90" />
            </summary>
            <NameList items={succeeded} />
          </details>
        )}
      </div>
    </>
  )
}
